import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type {
  CreateTemplateInput,
  TemplateMediaKind,
  UpdateTemplateInput,
  WabaTemplate,
  WabaTemplateEvent,
  WabaTemplateMedia,
} from "@school-sales-ai/shared";
import { api } from "@/lib/api";
import { useScopedClientId } from "@/hooks/useScopedClientId";

const BASE = "/crm/whatsapp/templates";

/**
 * The template list.
 *
 * `staleTime: 0` for the same reason `useVisitSlots` uses it — this state changes
 * under our feet. Meta approves, rejects, recategorizes and pauses templates on
 * its own schedule, so a cached list is a list that is quietly wrong.
 */
export function useWabaTemplates(includeArchived = false) {
  const clientId = useScopedClientId();
  return useQuery({
    queryKey: ["waba-templates", clientId, includeArchived],
    queryFn: () =>
      api
        .get<{ templates: WabaTemplate[] }>(
          `${BASE}?includeArchived=${String(includeArchived)}`,
        )
        .then((r) => r.templates),
    enabled: !!clientId,
    staleTime: 0,
  });
}

/**
 * Uploads a file for a template's media header.
 *
 * Deliberately NOT part of the editor's dirty/save cycle — the same call
 * `LogoSettings` makes and for the same reason: a multipart upload cannot ride a
 * form's submit, so it commits immediately and hands back an asset id the form
 * then carries. One endpoint serves both the template's sample and a
 * broadcast's per-send replacement.
 */
export function useUploadTemplateMedia() {
  return useMutation({
    mutationFn: ({ file, kind }: { file: File; kind: TemplateMediaKind }) => {
      const form = new FormData();
      // The text field goes FIRST so the backend has parsed it by the time it
      // reads the file stream — the `uploadDocumentApi` ordering rule.
      form.append("kind", kind);
      form.append("file", file);
      return api.postForm<WabaTemplateMedia>(`${BASE}/media`, form);
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useWabaTemplateEvents(templateId: string | null) {
  const clientId = useScopedClientId();
  return useQuery({
    queryKey: ["waba-templates", clientId, templateId, "events"],
    queryFn: () =>
      api
        .get<{ events: WabaTemplateEvent[] }>(`${BASE}/${templateId as string}/events`)
        .then((r) => r.events),
    enabled: !!clientId && !!templateId,
  });
}

/**
 * Shared mutation factory — mirrors `useVisitAction`. Every template mutation
 * invalidates the whole list, because Meta's answer can change more than the row
 * that was acted on (a category sweep, for instance).
 */
function useTemplateAction<TVars, TResult>(
  mutationFn: (vars: TVars) => Promise<TResult>,
  successMessage: string,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["waba-templates"] });
      toast.success(successMessage);
    },
    onError: (err: Error) => {
      // The message is Meta's own `error_user_msg` whenever Graph refused —
      // written for a person to read, so it is shown rather than replaced.
      toast.error(err.message);
    },
  });
}

export function useCreateWabaTemplate() {
  return useTemplateAction(
    (input: CreateTemplateInput) => api.post<WabaTemplate>(BASE, input),
    "Plantilla guardada como borrador.",
  );
}

export function useUpdateWabaTemplate() {
  return useTemplateAction(
    ({ id, input }: { id: string; input: UpdateTemplateInput }) =>
      api.patch<WabaTemplate>(`${BASE}/${id}`, input),
    "Plantilla actualizada.",
  );
}

export function useSubmitWabaTemplate() {
  return useTemplateAction(
    (id: string) => api.post<WabaTemplate>(`${BASE}/${id}/submit`, {}),
    "Enviada a Meta. La revisión suele tardar unos minutos.",
  );
}

export function useArchiveWabaTemplate() {
  return useTemplateAction(
    (id: string) => api.delete<void>(`${BASE}/${id}`),
    "Plantilla archivada.",
  );
}

export function useUnarchiveWabaTemplate() {
  return useTemplateAction(
    (id: string) => api.post<WabaTemplate>(`${BASE}/${id}/unarchive`, {}),
    "Plantilla desarchivada.",
  );
}

export function useSyncWabaTemplates() {
  return useTemplateAction(
    () => api.post<{ synced: number }>(`${BASE}/sync`, {}),
    "Sincronizado con Meta.",
  );
}

/** Super-admin only. The 30-day name lock makes this genuinely destructive. */
export function useDeleteWabaTemplateInMeta() {
  return useTemplateAction(
    (id: string) => api.delete<void>(`${BASE}/${id}/meta`),
    "Plantilla eliminada en Meta.",
  );
}
